import type { ReactNode } from 'react'
import RenderHTML from '../../../components/ui-components/RenderHTML'

export type ConnectedSummaryLine = {
  html?: string
  /** React phrase — use instead of html when a span must stay unsplit (KeepTogether). */
  content?: ReactNode
  onClick?: () => void
}

export type ConnectedSummaryLinesInput = ConnectedSummaryLine | string

export type ConnectedSummaryLinesProps = {
  lines: ConnectedSummaryLinesInput[]
  connectorIcon?: string
  connectorColor?: string
  connectorThickness?: number
  lineHeight?: number
  gap?: number
  className?: string
  lineClassName?: string
}

const DEFAULT_CONNECTOR_COLOR = '#F0F4F7'
const DEFAULT_CONNECTOR_THICKNESS = 1.5
const DEFAULT_LINE_HEIGHT = 4
const DEFAULT_GAP = 0

export function ConnectedSummaryLines({
  lines,
  connectorIcon,
  connectorColor = DEFAULT_CONNECTOR_COLOR,
  connectorThickness = DEFAULT_CONNECTOR_THICKNESS,
  lineHeight = DEFAULT_LINE_HEIGHT,
  gap = DEFAULT_GAP,
  className = '',
  lineClassName = '',
}: ConnectedSummaryLinesProps) {
  const connectorRailHeight = lineHeight + gap
  if (lines.length === 0) {
    return null
  }

  const connectorIconHtml =
    connectorIcon ?? buildDefaultConnectorIconSvg(connectorColor, connectorThickness)
  const showConnectorRail = lines.length > 1
  const normalizedLines = lines.map((line) =>
    typeof line === 'string' ? { html: line } : line
  )

  return (
    <div className={`flex pt-5 pl-3 ${className}`.trim()}>
      <div className="flex min-h-0 flex-col text-[13px] text-[#6A6E6E]" style={{ gap }}>
        {normalizedLines.map((line, lineIndex) => (
          <div
            key={lineIndex}
            className={`flex ${line.onClick ? 'cursor-pointer' : ''} ${lineClassName}`.trim()}
            style={{ minHeight: lineHeight }}
            onClick={line.onClick}
          >
            <div
              className="flex shrink-0 flex-col items-start"
              style={{ marginLeft: -connectorThickness, width: 12 }}
            >
              <div className="-mt-[10px] flex-shrink-0">
                <RenderHTML innerHTMLText={connectorIconHtml} />
              </div>
              {showConnectorRail && lineIndex < lines.length - 1 ? (
                <div
                  className="border-l border-solid"
                  style={{
                    marginTop: '-5px',
                    height: connectorRailHeight,
                    borderLeftWidth: connectorThickness,
                    borderLeftColor: connectorColor,
                  }}
                />
              ) : null}
            </div>
            <div className="ml-[6px] min-w-0 flex-1">{renderConnectedSummaryLine(line)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** HTML string or React content for one summary row. */
export function renderConnectedSummaryLine(line: ConnectedSummaryLine) {
  if (line.content != null) return line.content
  return <RenderHTML className="min-w-0" innerHTMLText={line.html ?? ''} />
}

export function buildDefaultConnectorIconSvg(color: string, thickness: number): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="22" viewBox="0 0 12 22" fill="none">
  <path d="M0.75 0.75V16.75C0.75 18.9591 2.54086 20.75 4.75 20.75H10.75" stroke="${color}" stroke-width="${thickness}" stroke-linecap="round"/>
</svg>`
}
